import { Component, computed, inject, input, signal } from '@angular/core';
import { HlmIconComponent } from './icon.component';
import { cn } from './cn';

/**
 *   <hlm-accordion>
 *     <hlm-accordion-item title="General" [expanded]="true">...</hlm-accordion-item>
 *     <hlm-accordion-item title="Advanced">...</hlm-accordion-item>
 *   </hlm-accordion>
 */
@Component({
  selector: 'hlm-accordion',
  standalone: true,
  template: '<ng-content />',
  host: { '[class]': '_c()' },
})
export class HlmAccordionComponent {
  readonly userClass = input<string>('', { alias: 'class' });
  readonly single = input(false); // only one item open at a time

  private openId = signal<string | null>(null);

  protected _c = computed(() =>
    cn('flex flex-col divide-y divide-border rounded-xl border border-border bg-card', this.userClass()),
  );

  register(id: string, defaultOpen: boolean): () => boolean {
    if (defaultOpen && this.single() && !this.openId()) this.openId.set(id);
    return () => {
      if (this.single()) return this.openId() === id;
      return defaultOpen;
    };
  }

  toggle(id: string, currentlyOpen: boolean, setOpen: (v: boolean) => void): void {
    if (this.single()) {
      this.openId.set(this.openId() === id ? null : id);
    } else {
      setOpen(!currentlyOpen);
    }
  }
}

@Component({
  selector: 'hlm-accordion-item',
  standalone: true,
  imports: [HlmIconComponent],
  template: `
    <button
      type="button"
      (click)="toggle()"
      class="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40"
    >
      <span class="flex-1 text-sm font-medium text-foreground">{{ title() }}</span>
      <hlm-icon
        [name]="isOpen() ? 'lucideChevronUp' : 'lucideChevronDown'"
        size="1rem"
        class="text-muted-foreground"
      />
    </button>
    @if (isOpen()) {
      <div class="px-4 pb-4 text-sm text-foreground">
        <ng-content />
      </div>
    }
  `,
})
export class HlmAccordionItemComponent {
  readonly title = input.required<string>();
  readonly expanded = input(false);
  readonly id = input<string>(crypto.randomUUID());

  private parent = inject(HlmAccordionComponent);
  private _localOpen = signal<boolean>(false);

  constructor() {
    this._localOpen.set(this.expanded());
    this.parent.register(this.id(), this.expanded());
  }

  isOpen(): boolean {
    return this.parent['single']() ? this.parent['openId']() === this.id() : this._localOpen();
  }

  toggle(): void {
    this.parent.toggle(this.id(), this.isOpen(), v => this._localOpen.set(v));
  }
}

export const HLM_ACCORDION = [HlmAccordionComponent, HlmAccordionItemComponent] as const;
